'use client'

import React from 'react'
import fp from 'lodash/fp'
import {useTranslations} from 'next-intl'
import {css, useTheme} from '@emotion/react'

import {ProcessStatus} from '../../../../constants'
import delay from '../../../../utils/delay'
import generateRandomString from './generateRandomString'
import ProcessStatusInfo from '../../../../components/modules/ProcessStatusInfo'
import Paper from '../../../../components/elements/Paper'
import NumberField from '../../../../components/elements/NumberField'
import Checkbox from '../../../../components/elements/Checkbox'
import Button from '../../../../components/elements/Button'
import InputResultPanelLayout from '../../../../components/layouts/InputResultPanelLayout'
import DataExportControl from '../../../../components/modules/DataExportControl'
import TextEditor from '../../../../components/modules/TextEditor'
import TextField from '../../../../components/elements/TextField'


const CheckboxStatus = Object.freeze({
  Checked: 'Checked',
  Indeterminate: 'Indeterminate',
  Unchecked: 'Unchecked',
})

const InclusionStatus = Object.freeze({
  FullyIncluded: 'FullyIncluded',
  PartiallyIncluded: 'PartiallyIncluded',
  NotIncluded: 'NotIncluded',
})

const LowerCaseCharacters = 'abcdefghijklmnopqrstuvwxyz'
const UpperCaseCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const NumericDigits = '0123456789'
const Symbols = '!"#$%&\\\'() *+,-./:;<=>?@[]^_`{|}~'


export default function RandomStringGenerator() {
  const theme = useTheme()
  const styles = {
    inputPanel: css`
      padding: 16px;
      background: ${theme.palette.surface.main};

      display: flex;
      flex-direction: column;

      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 8px;
      }
    `,

    control: css`
      padding: 16px;


      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 8px;
      }
    `,

    numberInput: css`
      margin-top: 8px;
    `,

    optionsControl: css`
      margin-top: 8px;
      padding: 8px 16px 8px 8px;
    `,

    bottom: css`
      margin-top: 12px;
      position: relative;
    `,

    button: css`
      width: 100%;
      font-size: 1.3em;
      padding: 4px 16px;
    `,

    status: css`
      position: absolute;
      top: 4px;
      right: 12px;
    `,

    bottomControlBar: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      height: 36px;
    `,
  }

  const t = useTranslations()

  const [outputResult, setOutputResult] = React.useState('')
  const onOutputResultChange = (outputResult) => {
    setOutputResult(outputResult)
  }

  const [advanceMode, setAdvanceMode] = React.useState(false)
  const onAdvanceModeChange = (checked) => {
    setAdvanceMode(checked)
  }

  const [lowerCaseCharacters, setLowerCaseCharacters] = React.useState(LowerCaseCharacters)
  const [upperCaseCharacters, setUpperCaseCharacters] = React.useState('')
  const [numericDigits, setNumericDigits] = React.useState('')
  const [symbols, setSymbols] = React.useState('')

  const characters = React.useMemo(() => (
    lowerCaseCharacters + upperCaseCharacters + numericDigits + symbols
  ), [lowerCaseCharacters, upperCaseCharacters, numericDigits, symbols])

  const [options, setOptions] = React.useState({
    lengthOfString: 10,
    numberOfStrings: 1,
  })
  const onOptionsChange = (options) => {
    setOptions(options)
    setProcessStatus(ProcessStatus.Idle)
  }

  const isValid = (
    characters &&
    options.lengthOfString > 0 &&
    options.numberOfStrings > 0
  )

  const [processStatus, setProcessStatus] = React.useState(ProcessStatus.Idle)
  const onGenerateButtonClick = async () => {
    await setProcessStatus(ProcessStatus.Processing)
    await delay(500)

    let result = ''
    for (let i = 0; i < options.numberOfStrings; i++) {
      result += await generateRandomString(options.lengthOfString, {characters})
      result += '\n'
    }

    await setOutputResult(result)
    await setProcessStatus(ProcessStatus.Success)
  }

  return (
    <InputResultPanelLayout
      inputContent={(
        <div>
          <Paper css={styles.inputPanel}>
            <Paper css={styles.control}>
              <div css={css`
                display: flex;
                align-items: end;
                gap: 4px;
              `}>
                <NumberField
                  label={t('random-string-generator.length-of-string')}
                  css={styles.numberInput}
                  value={options.lengthOfString}
                  onChange={value => onOptionsChange({...options, lengthOfString: value})}
                />
                <div css={css`
                  font-size: 1.5em;
                `}>×
                </div>
                <NumberField
                  label={t('random-string-generator.number-of-strings')}
                  css={styles.numberInput}
                  value={options.numberOfStrings}
                  onChange={value => onOptionsChange({...options, numberOfStrings: value})}
                />
              </div>

              <div css={styles.optionsControl}>
                <div css={css`
                  display: flex;
                  justify-content: right;
                `}>
                  <Checkbox
                    label={t('random-string-generator.advanced-mode')}
                    checked={advanceMode}
                    onChange={onAdvanceModeChange}
                  />
                </div>
                <div css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                `}>
                  <CheckboxArea
                    label={t('random-string-generator.use-lowercase-letters')}
                    advanceMode={advanceMode}
                    characters={lowerCaseCharacters}
                    onCharactersChange={setLowerCaseCharacters}
                    allCharacters={LowerCaseCharacters}
                  />
                  <CheckboxArea
                    label={t('random-string-generator.use-uppercase-letters')}
                    advanceMode={advanceMode}
                    characters={upperCaseCharacters}
                    onCharactersChange={setUpperCaseCharacters}
                    allCharacters={UpperCaseCharacters}
                  />
                  <CheckboxArea
                    label={t('random-string-generator.use-numeric-digits')}
                    advanceMode={advanceMode}
                    characters={numericDigits}
                    onCharactersChange={setNumericDigits}
                    allCharacters={NumericDigits}
                  />
                  <CheckboxArea
                    label={t('random-string-generator.use-symbols')}
                    advanceMode={advanceMode}
                    characters={symbols}
                    onCharactersChange={setSymbols}
                    allCharacters={Symbols}
                  />
                </div>
              </div>
            </Paper>

            <div css={styles.bottom}>
              <Button
                css={styles.button}
                disabled={!isValid}
                onClick={onGenerateButtonClick}>
                {t('random-string-generator.generate')}
              </Button>
              <ProcessStatusInfo css={styles.status} enableProgressing processStatus={processStatus}/>
            </div>
          </Paper>
        </div>
      )}

      resultContent={(
        <TextEditor
          id="text-output"
          loading={processStatus === ProcessStatus.Processing}
          value={outputResult instanceof Error ? outputResult.toString() : outputResult}
          onChange={onOutputResultChange}

          extraControl={(
            <div css={styles.bottomControlBar}>
              <DataExportControl
                disabled={!outputResult || outputResult instanceof Error}
                data={outputResult}

                downloadButtonId={'download-button'}
              />
            </div>
          )}
        />
      )}
    />
  )
}

function CheckboxArea({label, advanceMode, characters, onCharactersChange, allCharacters}) {
  const onCharactersChangeWrapped = (v) => {
    if (fp.pullAll(allCharacters)(v).length === 0) {
      onCharactersChange(v)
    }
  }

  const addToCharacters = () => {
    const result = fp.flow(
      fp.uniq,
      fp.sortBy(v => v),
    )(characters + allCharacters).join('')
    onCharactersChange(result)
  }

  const removeFromCharacters = () => {
    const result = fp.flow(
      fp.uniq,
      fp.pullAll(allCharacters),
      fp.sortBy(v => v),
    )(characters).join('')
    onCharactersChange(result)
  }

  const getInclusionStatus = () => {
    const count = fp.flow(
      fp.pullAll(characters),
      fp.uniq,
    )(allCharacters).length

    if (count === 0) {
      return InclusionStatus.FullyIncluded
    } else if (count === allCharacters.length) {
      return InclusionStatus.NotIncluded
    } else {
      return InclusionStatus.PartiallyIncluded
    }
  }

  const [checkboxStatus, setCheckboxStatus] = React.useState(CheckboxStatus.Unchecked)
  const onCheckboxChange = () => {
    switch (checkboxStatus) {
      case CheckboxStatus.Unchecked:
      case CheckboxStatus.Indeterminate:
        addToCharacters(LowerCaseCharacters)
        return
      case CheckboxStatus.Checked:
        removeFromCharacters(LowerCaseCharacters)
        return
    }
  }

  React.useEffect(() => {
    const inclusionStatus = getInclusionStatus(LowerCaseCharacters)

    switch (inclusionStatus) {
      case InclusionStatus.NotIncluded:
        setCheckboxStatus(CheckboxStatus.Unchecked)
        return
      case InclusionStatus.PartiallyIncluded:
        setCheckboxStatus(CheckboxStatus.Indeterminate)
        return
      case InclusionStatus.FullyIncluded:
        setCheckboxStatus(CheckboxStatus.Checked)
        return
    }
  }, [characters])

  return (
    <div>
      <Checkbox
        checked={checkboxStatus === CheckboxStatus.Checked}
        indeterminate={checkboxStatus === CheckboxStatus.Indeterminate}
        onChange={onCheckboxChange}
        label={label}
      />
      {advanceMode ? (
        <TextField
          css={css`
            height: 36px;
          `}
          disabled={checkboxStatus === CheckboxStatus.Unchecked}
          value={characters}
          onChange={onCharactersChangeWrapped}
        />
      ) : null}
    </div>
  )
}